/** <shop-the-look-item>: one product of the « Voir le look » drawer, self-contained so several can coexist — sizes select the variant (an unavailable one stays clickable), add posts to /cart/add.js then emits CartAddEvent, sold out shows an inert button with no Klaviyo, and a color swatch swaps the item in place through the Section Rendering API. */
import { CartAddEvent } from "@theme/events";
import { cartSectionIds } from "@theme/utilities";

class ShopTheLookItem extends HTMLElement {
  #adding = false;
  /** @type {AbortController|null} */
  #abortController = null;

  connectedCallback() {
    this.#cacheRefs();
    this.addEventListener("click", this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
    this.#abortController?.abort();
  }

  /** (Re)caches the internal refs; called on connect AND after a color switch, since the content is replaced. */
  #cacheRefs() {
    this.addButton = this.querySelector("[data-shop-the-look-add]");
    this.unavailableButton = this.querySelector("[data-shop-the-look-bis]");
    this.sizes = Array.from(this.querySelectorAll("[data-shop-the-look-size]"));
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    // Pastille couleur → remplace l'item par le produit de la couleur cliquée.
    const color = event.target.closest("[data-shop-the-look-color]");
    if (color && this.contains(color)) {
      this.#handleColorClick(event, color);
      return;
    }

    const size = event.target.closest("[data-shop-the-look-size]");
    if (size && this.contains(size)) {
      this.#selectSize(size);
      return;
    }

    const addButton = event.target.closest("[data-shop-the-look-add]");
    if (addButton && this.contains(addButton) && !addButton.hidden) {
      event.preventDefault();
      this.#addToCart(addButton);
    }
  };

  /**
   * Selects a size: updates the active state and switches the action zone on variant availability, like product-availability.js on the PDP.
   * @param {HTMLElement} size
   */
  #selectSize(size) {
    this.sizes.forEach((button) => {
      const active = button === size;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });

    const variantId = size.dataset.variantId;
    const available = size.dataset.available !== "false";

    if (this.addButton) {
      this.addButton.dataset.variantId = variantId;
      this.addButton.hidden = !available;
    }

    // Bouton indisponible (Coming soon / rupture) : simple bascule de
    // visibilité, il est inerte et ne pointe vers rien.
    if (this.unavailableButton) {
      this.unavailableButton.hidden = available;
    }

    // Voile sur le visuel : rendu côté serveur d'après la dispo du PRODUIT,
    // il suit ensuite la variante sélectionnée (atténué si indispo, net sinon).
    this.classList.toggle("sold_out", !available);
  }

  /**
   * Adds the selected variant to the cart then notifies the theme: the cart drawer refreshes and opens, the cart icon updates.
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    if (this.#adding) return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#adding = true;
    button.setAttribute("aria-busy", "true");
    button.setAttribute("disabled", "");

    try {
      const sections = cartSectionIds();
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          ...(sections.length && { sections: sections.join(","), sections_url: window.location.pathname }),
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cart = await response.json();
      document.dispatchEvent(new CartAddEvent(cart, this.dataset.productId, { variantId, sections: cart.sections }));
    } catch (error) {
      console.error("[shop-the-look] add to cart failed:", error);
    } finally {
      this.#adding = false;
      button.removeAttribute("aria-busy");
      button.removeAttribute("disabled");
    }
  }

  /**
   * Click on a color swatch: fetches the linked product's item through the Section Rendering API and replaces THIS item's content; without JS the link keeps its native behaviour.
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} link
   */
  #handleColorClick = async (event, link) => {
    // Modificateurs (nouvel onglet, etc.) : on laisse le lien natif agir.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    // Couleur déjà affichée : rien à faire.
    if (link.classList.contains("is-active")) return;

    const targetHref = link.getAttribute("href");
    const targetHandle = this.#productHandle(targetHref);

    // Handle illisible → on ne peut pas garantir le bon produit : lien natif.
    if (!targetHandle) {
      window.location.assign(targetHref);
      return;
    }

    // URL reconstruite sur la racine localisée courante (cf. product-card.js) :
    // évite qu'un préfixe de marché/langue manquant fasse perdre ?section_id.
    const root = window.Shopify?.routes?.root || "/";
    const sectionUrl = `${root}products/${targetHandle}?section_id=shop-the-look-item-ajax`;

    // Annule un fetch en attente si l'utilisateur clique vite ; timeout de 2 s.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const controller = this.#abortController;
    const timer = setTimeout(() => controller.abort(), 2000);

    try {
      const response = await fetch(sectionUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const items = new DOMParser().parseFromString(html, "text/html").querySelectorAll("shop-the-look-item");

      // Garde-fou : on sélectionne l'item dont le handle correspond au lien
      // cliqué (et jamais le premier venu).
      const newItem = [...items].find((item) => this.#productHandle(item.getAttribute("data-product-url")) === targetHandle);

      if (!newItem) {
        console.warn("[shop-the-look] switch couleur : item introuvable → navigation native.");
        window.location.assign(targetHref);
        return;
      }

      this.#swapContent(newItem);
    } catch (error) {
      if (error.name === "AbortError") return;
      // Réseau/timeout : on ne bloque pas l'utilisateur, on suit le lien.
      console.error("[shop-the-look] échec du switch couleur:", error);
      window.location.assign(targetHref);
    } finally {
      clearTimeout(timer);
    }
  };

  /**
   * Replaces this item's CONTENT while keeping the same <shop-the-look-item> node, so the parent <li> does not move; the connectedCallback does not fire again, hence the re-cached refs.
   * @param {Element} newItem
   */
  #swapContent(newItem) {
    this.replaceChildren(...newItem.childNodes);
    // Le noeud hôte n'est pas remplacé : on reporte le voile `sold_out` du
    // nouveau produit, sinon l'état de la couleur précédente resterait collé.
    this.classList.toggle("sold_out", newItem.classList.contains("sold_out"));
    this.dataset.productId = newItem.getAttribute("data-product-id") ?? "";
    this.dataset.productUrl = newItem.getAttribute("data-product-url") ?? "";
    this.#adding = false;
    this.#cacheRefs();
  }

  /**
   * Extracts the handle from a Shopify product URL, robust to language prefixes, query and trailing slash.
   * @param {string|null} path
   * @returns {string}
   */
  #productHandle(path) {
    const match = (path || "").match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  }
}

if (!customElements.get("shop-the-look-item")) {
  customElements.define("shop-the-look-item", ShopTheLookItem);
}
